import axios from 'axios';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

const API = `${process.env.REACT_APP_API_BASE_URL}/property-finder/guest/sessions`;

export const saveSessionData = async (sessionUuid, data) => {
  const resp = await axios.put(`${API}/${sessionUuid}`, data);

  return resp.data;
};

export const getSession = async (sessionId) => {
  const response = await axios.get(`${API}/${sessionId}`);

  return {
    messages: response.data.messages || [],
    last_json_uuid_reference: response.data.last_json_uuid_reference || null,
    loaded_json_uuid_reference: response.data.loaded_json_uuid_reference || null,
    search_summary: response.data.search_summary || null,
    properties_feedback: response.data.properties_feedback || null,
    user_filters: response.data.user_filters || null,
    filter_context: response.data.filter_context || null,
    parsed_json: response.data.parsed_json || null
  };
};

export const getProperties = async (sessionId, cursor, userFilters = null) => {
  const url = `${API}/${sessionId}/properties/search`;

  const params = {};

  // Add cursor if provided (for subsequent pages)
  if (cursor) {
    params.cursor = cursor;
  }

  // Add filter parameters if provided
  if (userFilters) {
    if (userFilters.location) params.location = userFilters.location;
    if (userFilters.priceMin) params.priceMin = userFilters.priceMin;
    if (userFilters.priceMax) params.priceMax = userFilters.priceMax;
    if (userFilters.bedrooms) params.bedrooms = userFilters.bedrooms;
    if (userFilters.bathrooms) params.bathrooms = userFilters.bathrooms;
    if (userFilters.areaMin) params.areaMin = userFilters.areaMin;
    if (userFilters.areaMax) params.areaMax = userFilters.areaMax;

    // For array filters: send '_none_' when explicitly empty (user cleared all)
    // This distinguishes from undefined (not set) vs [] (user cleared all)
    if (Array.isArray(userFilters.propertyTypes)) {
      params.propertyTypes =
        userFilters.propertyTypes.length > 0 ? userFilters.propertyTypes.join(',') : '_none_';
    }
    if (Array.isArray(userFilters.conditions)) {
      params.conditions =
        userFilters.conditions.length > 0 ? userFilters.conditions.join(',') : '_none_';
    }
    if (Array.isArray(userFilters.features)) {
      params.features = userFilters.features.length > 0 ? userFilters.features.join(',') : '_none_';
    }
  }
  return await axios.get(url, { params });
};

export const getFavouriteProperties = async (sessionId) => {
  const url = `${API}/${sessionId}/properties/favourite`;

  return await axios.get(url);
};

export const cacheProperty = async (sessionId, propertyId, data) => {
  const url = `${API}/${sessionId}/properties/${propertyId}/cache`;

  return await axios.post(url, data);
};

export const removeCachedProperty = async (sessionId, propertyId) => {
  const url = `${API}/${sessionId}/properties/${propertyId}/favourite`;

  return await axios.delete(url);
};

export const useSession = ({ enabled, sessionId }) =>
  useQuery({
    queryKey: ['propertyFinderSession', sessionId],
    queryFn: async () => {
      const response = await getSession(sessionId);

      return response;
    },
    enabled
  });

export const useProperties = ({ enabled, sessionId, userFilters = null }) =>
  useInfiniteQuery({
    // Include userFilters in queryKey so React Query treats different filters as different queries
    // This ensures fresh data is fetched when filters change
    queryKey: ['propertyFinderSessionProperties', sessionId, userFilters],
    queryFn: async ({ pageParam = null }) => {
      const response = await getProperties(sessionId, pageParam, userFilters);

      return response.data;
    },
    getNextPageParam: (lastPage) => {
      // If nextCursor exists, return it for the next page
      if (lastPage?.nextCursor) {
        return lastPage.nextCursor;
      }
      // No more pages
      return undefined;
    },
    enabled,
    // Don't use stale data - always fetch fresh when filters change
    staleTime: 0,
    // Don't keep previous data when queryKey changes (filters change)
    keepPreviousData: false,
    refetchOnWindowFocus: false
  });

export const useFavouriteProperties = ({ enabled, sessionId }) =>
  useQuery({
    queryKey: ['propertyFinderSessionFavouriteProperties', sessionId],
    queryFn: async () => {
      const response = await getFavouriteProperties(sessionId);

      return response.data;
    },
    enabled
  });

// Update USER filters in parsed_json.user_filters (separate from AI filters)
export const updateUserFilters = async (sessionId, filters) => {
  const url = `${API}/${sessionId}/user-filters`;

  return await axios.put(url, { filters });
};

// Update session filters in parsed_json.properties_data (AI filters)
export const updateSessionFilters = async (sessionId, filters) => {
  const url = `${API}/${sessionId}/filters`;

  return await axios.put(url, { filters });
};

// New function for AI context synchronization
export const updateSessionContext = async (sessionId, { userFilters, messages }) => {
  const url = `${API}/${sessionId}/context`;

  return await axios.put(url, {
    userFilters,
    messages
  });
};
